/**
 * <InsightsAtlasPage> - Iter 9.40 Pass E
 *
 * Admin-only Insights Atlas dashboard. Reads from
 *   GET /api/admin/insights/atlas
 *   GET /api/admin/insights/preferences/{category_id}
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, Calendar, Sparkles, Image as ImageIcon, Star, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatTile = ({ label, value, icon: Icon, color }) => (
  <Card data-testid={`atlas-tile-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-xs uppercase font-medium text-gray-500">
        {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
        {label}
      </div>
      <div className={`text-3xl font-bold mt-2 ${color || "text-gray-900"}`}>{value ?? "-"}</div>
    </CardContent>
  </Card>
);

export default function InsightsAtlasPage() {
  const [atlas, setAtlas] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAtlas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/insights/atlas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setAtlas(await res.json());
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchAtlas(); }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-10" data-testid="atlas-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!atlas) {
    return <div className="py-10 text-center text-sm text-gray-500" data-testid="atlas-error">Failed to load atlas.</div>;
  }

  const h = atlas.headline || {};

  return (
    <div className="space-y-6" data-testid="insights-atlas-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> Insights Atlas
          </h2>
          <p className="text-sm text-gray-500">Read-only analytics across preferences, inspo, brand pilots and engagement.</p>
        </div>
        <button onClick={fetchAtlas} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="atlas-refresh">
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Users"          value={h.users}         icon={Users} />
        <StatTile label="Events"         value={h.events}        icon={Calendar} />
        <StatTile label="Experiences"    value={h.experiences}   icon={Sparkles} />
        <StatTile label="RSVPs"          value={h.rsvps}         icon={Users} />
        <StatTile label="Preferences"    value={h.preferences}   icon={Star} />
        <StatTile label="Host notes"     value={h.host_notes}    icon={Sparkles} />
        <StatTile label="Inspo folders"  value={h.inspo_folders} icon={ImageIcon} />
        <StatTile label="Pref clicks"    value={atlas.preference_clicks?.total} icon={Star} />
      </div>

      {/* Preference options drill-down */}
      <div>
        <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" /> Top preference options by category
        </h3>
        <p className="text-xs text-gray-500 mb-3">Counted across {atlas.preference_options?.users_with_prefs || 0} users with saved preferences.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(atlas.preference_options?.by_category || []).map((c) => (
            <Card key={c.category_id} data-testid={`atlas-cat-${c.category_id}`}>
              <CardContent className="p-4">
                <div className="font-medium text-sm capitalize mb-2">{c.category_id}</div>
                {c.top_options.length === 0 ? (
                  <p className="text-xs text-gray-400">No data yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {c.top_options.map((opt) => (
                      <div key={opt.key} className="flex items-center justify-between text-xs">
                        <span className="text-gray-700 truncate">{opt.key}</span>
                        <Badge variant="secondary">{opt.count}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Inspo + brand pilots */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card data-testid="atlas-inspo">
          <CardContent className="p-4">
            <div className="font-medium text-sm mb-2 flex items-center gap-1.5">
              <ImageIcon className="w-4 h-4" /> Inspo sources ({atlas.inspo?.total_images || 0} total)
            </div>
            <div className="space-y-1.5">
              {(atlas.inspo?.top_sources || []).map((s) => (
                <div key={s.source} className="flex items-center justify-between text-xs">
                  <span className="text-gray-700">{s.source}</span>
                  <Badge variant="secondary">{s.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="atlas-brand-pilots">
          <CardContent className="p-4">
            <div className="font-medium text-sm mb-2">Brand pilot enrollments ({atlas.brand_pilots?.total_active || 0} active)</div>
            {(atlas.brand_pilots?.by_brand || []).length === 0 ? (
              <p className="text-xs text-gray-400">No enrollments yet.</p>
            ) : (
              <div className="space-y-1.5">
                {(atlas.brand_pilots?.by_brand || []).map((b) => (
                  <div key={b.brand_id} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 capitalize">{b.brand_id}</span>
                    <Badge variant="secondary">{b.count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Iter 9.40 Pass F - Connections panel: Master Features × Launch Control × Categories */}
      <div data-testid="atlas-connections">
        <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
          🔗 Cross-system connections
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Master Features × Launch Control × Categories - confirms every category surfaces matching features at the right stage.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card data-testid="atlas-features-by-stage">
            <CardContent className="p-4">
              <div className="font-medium text-sm mb-2">Features by stage ({atlas.features?.total || 0})</div>
              <div className="space-y-1.5">
                {Object.entries(atlas.features?.by_stage || {}).map(([stage, count]) => (
                  <div key={stage} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 capitalize">{stage.replace(/_/g, " ")}</span>
                    <Badge
                      variant="secondary"
                      className={
                        stage === "live" ? "bg-green-50 text-green-700" :
                        stage === "future" ? "bg-blue-50 text-blue-700" :
                        ""
                      }
                    >
                      {count}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="atlas-launch-control">
            <CardContent className="p-4">
              <div className="font-medium text-sm mb-2">Launch Control by state</div>
              <div className="space-y-1.5">
                {Object.entries(atlas.launch_control?.by_state || {}).map(([state, count]) => (
                  <div key={state} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 capitalize">{state}</span>
                    <Badge
                      variant="secondary"
                      className={
                        state === "live" ? "bg-green-50 text-green-700" :
                        state === "waitlist" ? "bg-amber-50 text-amber-700" :
                        ""
                      }
                    >
                      {count}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-3" data-testid="atlas-cat-cross">
          <CardContent className="p-4 overflow-x-auto">
            <div className="font-medium text-sm mb-2">Per-category × feature stage</div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1 pr-3">Category</th>
                  <th className="py-1 pr-3">Launch state</th>
                  <th className="py-1 pr-3">Features</th>
                </tr>
              </thead>
              <tbody>
                {(atlas.launch_control?.categories || []).map((c) => (
                  <tr key={c.category_id} className="border-b last:border-b-0" data-testid={`atlas-cat-row-${c.category_id}`}>
                    <td className="py-1.5 pr-3 capitalize font-medium">{c.category_id}</td>
                    <td className="py-1.5 pr-3">
                      <Badge
                        variant="outline"
                        className={
                          c.launch_state === "live" ? "border-green-300 text-green-700" :
                          "border-amber-300 text-amber-700"
                        }
                      >
                        {c.launch_state}
                      </Badge>
                    </td>
                    <td className="py-1.5 pr-3 text-gray-600">
                      {Object.entries(c.feature_breakdown || {}).map(([stage, count]) => (
                        <span key={stage} className="mr-2">
                          {count} {stage}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
